// =============================================================================
// MeuDriver — useInstructorProfile
// Batch L1: hook para dados aprimorados do perfil do instrutor.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InstructorEnhancedFields {
  teaching_since: string | null       // DATE como string ISO
  specializations: string[]
  languages: string[]
  video_intro_url: string | null
  certifications: string[]
  highlights: string | null
}

export interface InstructorStats {
  total_lessons: number
  unique_students: number
  years_experience: number | null
  specialties: string[]
  rating_avg: number
}

export interface UpdateInstructorEnhancedPayload extends Partial<InstructorEnhancedFields> {
  instructorId: string
}

// ─── Keys ────────────────────────────────────────────────────────────────────

export const instructorProfileKeys = {
  enhanced: (instructorId: string) => ['instructor-enhanced', instructorId] as const,
  stats: (instructorId: string) => ['instructor-stats', instructorId] as const,
}

// ─── useInstructorEnhancedFields ─────────────────────────────────────────────
// Busca os campos enriquecidos (Batch L1) de um instrutor pelo ID da linha
// em instructors (não profile_id).

export function useInstructorEnhancedFields(instructorId: string | undefined) {
  return useQuery<InstructorEnhancedFields>({
    queryKey: instructorProfileKeys.enhanced(instructorId ?? ''),
    enabled: Boolean(instructorId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select('teaching_since, specializations, languages, video_intro_url, certifications, highlights')
        .eq('id', instructorId!)
        .single()

      if (error) throw error

      return {
        teaching_since: (data as any).teaching_since ?? null,
        specializations: (data as any).specializations ?? [],
        languages: (data as any).languages ?? [],
        video_intro_url: (data as any).video_intro_url ?? null,
        certifications: (data as any).certifications ?? [],
        highlights: (data as any).highlights ?? null,
      }
    },
  })
}

// ─── useUpdateInstructorEnhanced ─────────────────────────────────────────────

export function useUpdateInstructorEnhanced() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateInstructorEnhancedPayload) => {
      const { instructorId, ...fields } = payload
      const { error } = await supabase
        .from('instructors')
        .update(fields as Record<string, unknown>)
        .eq('id', instructorId)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: instructorProfileKeys.enhanced(variables.instructorId),
      })
    },
  })
}

// ─── useInstructorStats ───────────────────────────────────────────────────────
// Estatísticas públicas do instrutor: total de aulas, alunos únicos, experiência,
// especialidades e nota média.

export function useInstructorStats(instructorId: string | undefined) {
  return useQuery<InstructorStats>({
    queryKey: instructorProfileKeys.stats(instructorId ?? ''),
    enabled: Boolean(instructorId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      // Busca dados básicos do instrutor
      const { data: inst, error: instErr } = await supabase
        .from('instructors')
        .select('total_lessons, years_experience, rating_avg')
        .eq('id', instructorId!)
        .single()

      if (instErr) throw instErr

      // Alunos únicos (bookings concluídos)
      const { data: bookingData, error: bookingErr } = await supabase
        .from('bookings')
        .select('student_id')
        .eq('instructor_id', instructorId!)
        .eq('status', 'completed')

      if (bookingErr) throw bookingErr

      const uniqueStudents = new Set(
        (bookingData ?? []).map((b: { student_id: string }) => b.student_id)
      ).size

      // Especialidades
      const { data: specs } = await supabase
        .from('instructor_specialties')
        .select('specialty')
        .eq('instructor_id', instructorId!)

      return {
        total_lessons: (inst as any).total_lessons ?? 0,
        unique_students: uniqueStudents,
        years_experience: (inst as any).years_experience ?? null,
        specialties: ((specs ?? []) as { specialty: string }[]).map((s) => s.specialty),
        rating_avg: parseFloat(String((inst as any).rating_avg ?? 0)),
      }
    },
  })
}

// ─── useMyInstructorId ────────────────────────────────────────────────────────
// Retorna o ID da row em instructors do usuário autenticado.

export function useMyInstructorId() {
  const { user } = useAuth()

  return useQuery<string | null>({
    queryKey: ['my-instructor-id', user?.id ?? ''],
    enabled: Boolean(user?.id),
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .single()
      return (data as any)?.id ?? null
    },
  })
}
